import { Observable } from 'rxjs/Observable';
import { async } from 'rxjs/scheduler/async';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/throttleTime';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import log from '@atlassian/jira-common-util-logging/src/log';
import { fetchFilteredCardsService } from '../../../services/filtered-cards';
import { makeServiceContext } from '../../../services/service-context';
import { type SetAppDataAction, setAppData } from '../../../state/actions/app';
import {
	cardAutoScrollRequestOnFilter,
	type CardAutoScrollRequestOnFilterAction,
} from '../../../state/actions/cards/auto-scroll';
import {
	FILTERED_CARD_REQUEST,
	type Action as FilteredCardsAction,
	type FilteredCardRequestAction,
	filteredCardsSuccess,
	filteredCardsFailed,
	clearFilteredCards,
	clearFilteredCardsRequest,
} from '../../../state/actions/cards/filtered-cards';
import { setAllFilters, type Action as FiltersAction } from '../../../state/actions/filters';
import { getEpicIssueType } from '../../../state/selectors/app';
import type { ActionsObservable, Dependencies, MiddlewareAPI } from '../../../state/types';
import { getCustomFiltersDebounceTime } from '../utils';

const filterCards = (
	action: FilteredCardRequestAction,
	store: MiddlewareAPI,
	dependencies?: Dependencies,
): Observable<
	FilteredCardsAction | FiltersAction | SetAppDataAction | CardAutoScrollRequestOnFilterAction
> => {
	const state = store.getState();
	const { issueIds, customFilterIds } = action.payload;
	const { analyticsEvent } = action.meta;

	const ctx = makeServiceContext(state);

	if (!customFilterIds || !customFilterIds.length) {
		return Observable.from([
			ctx.isCompanyManaged ? clearFilteredCardsRequest(analyticsEvent) : clearFilteredCards(),
			cardAutoScrollRequestOnFilter(),
		]);
	}

	return fetchFilteredCardsService(ctx, {
		issueIds,
		customFilterIds,
		epicIssueType: getEpicIssueType(state),
		storeFieldsActions: dependencies?.storeFieldsActions,
	})
		.flatMap(({ filteredCardIds, data }) => [
			filteredCardsSuccess(
				filteredCardIds,
				customFilterIds.length,
				issueIds.length,
				analyticsEvent,
			),
			...(data ? [setAppData(data)] : []),
			cardAutoScrollRequestOnFilter(),
		])
		.catch((error) => {
			log.safeErrorWithoutCustomerData(
				'backlog.filtered-cards.failure',
				'Fail to filter cards with custom filters',
				error,
			);
			return Observable.of<FilteredCardsAction | FiltersAction>(
				filteredCardsFailed(error, analyticsEvent),
				setAllFilters({}),
			);
		});
};

export const filteredCardsRequestEpic = (
	action$: ActionsObservable,
	store: MiddlewareAPI,
	dependencies?: Dependencies,
) =>
	action$
		.ofType(FILTERED_CARD_REQUEST)
		.throttleTime(getCustomFiltersDebounceTime(), async, {
			leading: true,
			trailing: true,
		})
		.switchMap((action: FilteredCardRequestAction) => filterCards(action, store, dependencies));
